import { useLocation } from "react-router-dom";
import { useState, useCallback, useEffect, useMemo } from "react";
import {
  IconUser,
  IconColorFilter,
  IconShieldHalf,
  IconBuilding,
  IconUsers,
  IconCrown,
  IconAt,
  IconWorld,
  IconAddressBook,
  IconFileCertificate,
} from "@tabler/icons-react";
import { prefetchUserRoute } from "./routePrefetch";
import { useGuardedNavigate } from "@/hooks/useGuardedNavigate";

export const useUserSidebar = (
  t: (key: string) => string,
  role: string | null,
  customerType: string,
) => {
  const pathname = useLocation().pathname;
  const [currentUrl, setCurrentUrl] = useState(pathname);

  useEffect(() => {
    setCurrentUrl(pathname);
  }, [pathname]);

  const allMenuItems = [
    {
      name: t("profileSettings"),
      icon: IconUser,
      url: "settings",
    },
    {
      name: t("appearanceSettings"),
      icon: IconColorFilter,
      url: "theme",
    },
    {
      name: t("security"),
      icon: IconShieldHalf,
      url: "security",
    },
    {
      name: t(customerType === "COMPANY" ? "myCompany" : "myAccount"),
      icon: IconBuilding,
      url: "company",
    },
    {
      name: t("emailDomains"),
      icon: IconAt,
      url: "domains",
    },
    {
      name: t("portalDomain"),
      icon: IconWorld,
      url: "portal-domain",
    },
    {
      name: t("collaborators"),
      icon: IconUsers,
      url: "collaborators",
    },
    {
      name: t("crm"),
      icon: IconAddressBook,
      url: "crm",
    },
    // F29 — visible to ALL four company roles: OWNER/ADMIN read the tenant's
    // records, EDITOR/VIEWER read the ones they initiated themselves. So it
    // needs no rule in the useMemo below, and it must NOT be stripped from the
    // role===null loading shape either: a row nobody ever loses has to be
    // present while the role resolves, or it appears late.
    {
      name: t("privacy"),
      icon: IconFileCertificate,
      url: "privacy",
    },
    { name: t("subscription"), icon: IconCrown, url: "subscription" },
  ];

  // When role is null we are still loading. Show the maximum stable shape
  // EXCEPT the Owner-only "subscription" row — items only ever disappear
  // once role resolves, never appear late. This avoids the post-deploy
  // regression from commit 6d0ab51 where role-gated rows popped in after
  // a backend RTT.
  // "crm" is the second exception, for the OPPOSITE reason: it is hidden for
  // VIEWER, so including it in the loading shape would flash-then-vanish for
  // exactly that role. It only APPEARS once role resolves to O/A/E — a brief
  // absence for allowed roles is the lesser evil vs. flash-and-remove.
  const menuItems = useMemo(() => {
    if (role === null) {
      return allMenuItems.filter(
        (item) => item.url !== "subscription" && item.url !== "crm",
      );
    }
    return allMenuItems.filter((item) => {
      if (item.url === "subscription") {
        return role === "OWNER";
      }
      if (item.url === "crm") {
        // Company contact pool: OWNER/ADMIN full, EDITOR read-only, VIEWER none.
        return role === "OWNER" || role === "ADMIN" || role === "EDITOR";
      }
      if (role === "VIEWER" || role === "EDITOR") {
        return (
          item.url !== "company" &&
          item.url !== "domains" &&
          item.url !== "portal-domain" &&
          item.url !== "collaborators"
        );
      }
      return true;
    });
    // allMenuItems is rebuilt every render (cheap labels) — dep on role
    // + customerType captures the only inputs that change row identity.
  }, [role, customerType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Through the unsaved-changes guard (the role editor, the edit-member page
  // register one); `currentUrl` follows the pathname effect above, so a
  // cancelled navigation leaves the highlight where the user still is.
  const guardedNavigate = useGuardedNavigate();
  const navigateTo = useCallback(
    (url: string) => {
      guardedNavigate(url);
    },
    [guardedNavigate],
  );

  const prefetch = useCallback((url: string) => {
    prefetchUserRoute(url);
  }, []);

  return {
    menuItems,
    currentUrl,
    navigateTo,
    prefetch,
  };
};
